import React, { Component } from 'react';
import { Row } from 'reactstrap';
import styled from 'styled-components';
import { reduxForm } from 'redux-form';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import moment from 'moment';
import get from 'lodash/get';
import uuidv4 from 'uuid/v4';
import Well from 'components/Well';
import LargestCol from 'components/LargestCol';
import ReviewNote from 'components/ReviewNote';
import ButtonLink from 'components/ButtonLink';
import TermsAndConditionsModal from 'components/TermsAndConditionsModal';
import CreateBookingContainer from '../containers/CreateBookingContainer';
import EnteredGuestInfo from './EnteredGuestInfo';
import ReservationDetailsContainer from 'containers/ReservationDetailsContainer';
import BackButtonContainer from 'containers/BackButtonContainer';
import HostBookingDetailsContainer from 'containers/HostBookingDetailsContainer';
import { getBookingEnginePath, isHostBookingSource } from 'utils/routeUtils';
import { isMobileUserAgent } from 'utils/mobile';
import {
  PROPERTY_CODES,
  BOOKING_ENGINE_PAGE_CHANGED,
  PROPERTIES,
  FOOD_COMP,
  SPECIAL_OCCASION
} from 'utils/constants';
import { getThemeName } from 'utils/themeUtils';
import { selectedOfferShape, submittedAccountShape } from 'utils/shapes';
import {
  CANCELLATION_POLICY,
  PROPERTY_TERMS_AND_CONDITIONS_FULL
} from 'utils/messages/termsAndConditions';
import { gaEvent, gaModalView } from 'utils/googleAnalytics';
import LoadingIcon from './LoadingIcon';

const REVIEW_TAB = 'REVIEW_TAB';
const ADMIN_TAB = 'ADMIN_TAB';

const ReviewSubmitCol = styled(LargestCol)`
  & > h1,
  & > h2 {
    text-align: center;
    font-size: 1.5em;
  }

  & > p {
    text-align: center;
  }

  h5 {
    margin-bottom: 0;
  }

  label {
    display: block;
    margin-bottom: 0;
  }

  .row {
    margin-bottom: 0.75em;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .cancellation-policy {
    font-size: 0.6em;
    margin: 0;
    display: none;
  }

  .tab-pane {
    padding-top: 1em;
  }

  .nav-item {
    cursor: pointer;
  }

  @media screen and (min-width: ${props => props.theme.breakpoints.small}) {
    & > h1,
    & > h2 {
      font-size: 2rem;
    }

    & > h1,
    & > h2,
    & > p {
      text-align: left;
    }
  }

  @media screen and (min-width: ${props => props.theme.breakpoints.medium}) {
    .cancellation-policy {
      display: block;
    }
  }

  @media (max-width: ${props => props.theme.breakpoints.large}) {
    width: ${props => props.theme.fullWidth};
    margin: ${props => props.theme.center};
  }
`;

const ReservationCol = styled(LargestCol)`
  .cancellation-policy {
    display: block;
    font-size: 0.9em;
  }

  @media screen and (min-width: ${props => props.theme.breakpoints.medium}) {
    .cancellation-policy {
      display: none;
    }
  }

  @media (max-width: ${props => props.theme.breakpoints.large}) {
    max-width: ${props => props.theme.fullWidth};
    margin: ${props => props.theme.center};
  }
`;

class ReviewAndSubmit extends Component {
  state = {
    termsAndConditionsModalShown: false,
    activeTab: REVIEW_TAB,
    isMobile: false,
    transactionId: null
  };

  toggleTermsAndConditionsModal = () => {
    if (!this.state.termsAndConditionsModalShown) {
      // Record modal view in GA.
      gaModalView('terms-and-conditions');
    }

    this.setState({
      termsAndConditionsModalShown: !this.state.termsAndConditionsModalShown
    });
  };

  backButtonBeforeClick = e => {
    const { setShouldRefetch } = this.props;

    setShouldRefetch(true);
  };

  handleBookButtonClick = createBooking => {
    const {
      startDate,
      endDate,
      property,
      selectedRoom,
      numberOfGuests,
      guestInfo,
      submittedAccount,
      selectedOffers,
      goToConfirmationPage,
      bookingSource,
      bookingComments,
      secondSubmittedAccount,
      isAdmin,
      isWaitListBooking,
      waitListBookingComment,
      waitListBookingReason,
      goToWaitlistConfirmationPage
    } = this.props;

    const isHostBooking = isHostBookingSource(bookingSource);
    const formattedStartDate = moment(startDate).format('YYYY-MM-DD');
    const formattedEndDate = moment(endDate).format('YYYY-MM-DD');
    const alerts = get(guestInfo, 'alerts', []);
    const roomFeatures = get(guestInfo, 'roomFeatures', []);
    const traces = get(guestInfo, 'traces', []);
    const discounts = get(guestInfo, 'discounts', []);
    const specialRequests = get(guestInfo, 'specialRequests', []);
    const amenityRequests = get(guestInfo, 'amenityRequests', []);

    const booking = {
      booking: {
        propertyCode: PROPERTY_CODES[property],
        roomTypeCode: selectedRoom.roomType.roomTypeCodes.roomTypeCode,
        ratePlanCode:
          waitListBookingReason?.value === 'COMP'
            ? 'COMPHOST'
            : selectedRoom.roomRates[0].ratePlanCode,
        guestCount: numberOfGuests.value,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        creditCard: !(isHostBooking && isAdmin)
          ? {
              cardType: get(guestInfo, 'cardType.value'),
              cardHolderName: `${guestInfo.firstName} ${guestInfo.lastName}`,
              cardNumber: get(guestInfo, 'cardNumber')
                ? get(guestInfo, 'cardNumber')
                    .split(' ')
                    .join('')
                : null,
              cardCode: guestInfo.cvv,
              cardExpirationMonth: get(guestInfo, 'expirationMonth.value'),
              cardExpirationYear: get(guestInfo, 'expirationYear.value')
            }
          : null,
        guestProfile: {
          firstName: guestInfo.firstName,
          lastName: guestInfo.lastName,
          phone: guestInfo.phone,
          email: guestInfo.email,
          address: {
            addressLine: guestInfo.address,
            cityName: guestInfo.city,
            stateProv: guestInfo.state.value,
            countryCode: 'US',
            postalCode: guestInfo.postalCode
          }
        }
      },
      source: getThemeName(),
      bookingSource: bookingSource,
      additionalComments: bookingComments || null,
      alerts: alerts && alerts.length > 0 ? alerts.map(v => v.label) : null,
      roomFeatures:
        roomFeatures && roomFeatures.length > 0
          ? roomFeatures.map(v => v.value)
          : null,
      traces: traces && traces.length > 0 ? traces.map(v => v.label) : null,
      discounts:
        discounts && discounts.length > 0 ? discounts.map(v => v.label) : null,
      specialRequests:
        specialRequests && specialRequests.length > 0
          ? specialRequests.map(v => v.value)
          : null,
      amenityRequests:
        amenityRequests && amenityRequests.length > 0
          ? amenityRequests.map(v => v.value)
          : null
    };

    if (submittedAccount.accountNumber) {
      booking.patronAccountNumber = submittedAccount.accountNumber;
      booking.pin = submittedAccount.accountPin;
    }

    if (secondSubmittedAccount.accountNumber) {
      booking.secondPatronAccountNumber = secondSubmittedAccount.accountNumber;
    }

    if (selectedOffers && selectedOffers.length > 0) {
      booking.booking.promotionCodes = selectedOffers;
    }

    if (guestInfo.compCredit === FOOD_COMP) {
      booking.outletCredit =
        guestInfo.creditAmount + ' ' + guestInfo.creditOutlet;
    }

    if (guestInfo.specialOccasion === SPECIAL_OCCASION) {
      booking.specialOccasion = guestInfo.specialOccasionDetails;
    }

    let variables = { booking };

    const transactionId = uuidv4();

    if (isWaitListBooking) {
      variables = {
        booking: {
          createBooking: booking,
          externalConfirmationId: transactionId,
          waitlistRequestOptions: {
            waitlistReasonCode: get(waitListBookingReason, 'value', ''),
            waitlistReasonDescription: waitListBookingComment
          }
        }
      };
    }

    createBooking({ variables }).then(result => {
      if (get(result, 'data.createBooking.hotelReservation')) {
        const reservation = result.data.createBooking.hotelReservation;
        const total =
          reservation.roomStays &&
          reservation.roomStays[0] &&
          reservation.roomStays[0].total &&
          reservation.roomStays[0].total.amount;

        // Record GA event.
        gaEvent({
          category: 'Hotel Booking',
          action: 'Created Booking',
          label: `Property: ${PROPERTIES[property]}`
        });

        // Booking was successful, route to the confirmation page.
        goToConfirmationPage(result.data.createBooking);
      } else if (isWaitListBooking) {
        goToWaitlistConfirmationPage();
      }
    });

    if (isWaitListBooking) {
      this.setState({
        transactionId
      });
    }
  };

  handleReviewTabClick = () => {
    this.setState({
      activeTab: REVIEW_TAB
    });
  };

  handleAdminTabClick = () => {
    this.setState({
      activeTab: ADMIN_TAB
    });
  };

  componentDidMount() {
    const isMobile = isMobileUserAgent();
    const parentWindow = window.parent;

    if (isMobile) {
      this.handleMobileEvent(isMobile);
      window.addEventListener('mobile', this.handleMobileEvent);
    }

    parentWindow.postMessage(BOOKING_ENGINE_PAGE_CHANGED, '*');
  }

  componentWillUnmount() {
    if (window.removeEventListener) {
      window.removeEventListener('mobile', this.handleMobileEvent);
    }
  }

  handleMobileEvent = mobile => {
    const { isMobile } = this.state;

    if (isMobile !== mobile) {
      this.setState({
        isMobile: mobile
      });
    }
  };

  render() {
    const {
      guestInfo,
      property,
      isAdmin,
      bookingSource,
      reviewAndSubmitForm,
      isWaitListBooking
    } = this.props;
    const {
      termsAndConditionsModalShown,
      isMobile,
      transactionId
    } = this.state;

    const isHostBooking = isHostBookingSource(bookingSource);

    return (
      <React.Fragment>
        <Helmet>
          <title>Review & Submit</title>
        </Helmet>
        <Row className="reviewAndSubmit">
          <ReviewSubmitCol xs="12" md={isHostBooking ? 5 : 7}>
            {!guestInfo && <LoadingIcon />}
            {guestInfo && !(isWaitListBooking && transactionId) && (
              <React.Fragment>
                <h1>REVIEW &amp; SUBMIT</h1>
                <p>
                  Please take a moment to look over everything and make sure it
                  all looks good.
                </p>
                <EnteredGuestInfo
                  guestInfo={guestInfo}
                  property={property}
                  compCredit={
                    guestInfo.compCredit === FOOD_COMP
                      ? guestInfo.creditAmount + ' ' + guestInfo.creditOutlet
                      : null
                  }
                  toggleTermsAndConditionsModal={
                    this.toggleTermsAndConditionsModal
                  }
                  isHostBookingAdmin={isHostBooking && isAdmin}
                />
                <BackButtonContainer
                  className="back-button-middle"
                  to={getBookingEnginePath('guest-information')}
                />
              </React.Fragment>
            )}
          </ReviewSubmitCol>
          <ReservationCol xs="12" md={isHostBooking ? 4 : 5}>
            <Well admin={isAdmin}>
              <ReservationDetailsContainer
                discounts={reviewAndSubmitForm && reviewAndSubmitForm.discounts}
              />
            </Well>
            <ReviewNote className="mb-3" admin={isAdmin}>
              For COMP hotel bookings, ROOM TYPE PREFERENCE is not guaranteed
              until check in.
            </ReviewNote>
            <p className="cancellation-policy">
              {CANCELLATION_POLICY(property)}
            </p>
            {PROPERTY_TERMS_AND_CONDITIONS_FULL[property] && (
              <p className="cancellation-policy">
                <ButtonLink onClick={this.toggleTermsAndConditionsModal}>
                  See full terms and conditions
                </ButtonLink>
              </p>
            )}
            <CreateBookingContainer isMobile={isMobile} />
            <ReviewNote>
              Once you have submitted your reservation a confirmation email will
              be sent to the address provided.
            </ReviewNote>
          </ReservationCol>
          {isHostBooking && (
            <LargestCol md="12" lg="3">
              <HostBookingDetailsContainer
                isAdmin={isAdmin}
                isHostBooking={isHostBooking}
              />
            </LargestCol>
          )}
          {!isMobile && (
            <LargestCol xs="12">
              <BackButtonContainer
                className="back-button-bottom"
                to={getBookingEnginePath('guest-information')}
              />
            </LargestCol>
          )}
        </Row>
        <TermsAndConditionsModal
          property={property}
          isOpen={termsAndConditionsModalShown}
          toggle={this.toggleTermsAndConditionsModal}
        />
      </React.Fragment>
    );
  }
}

ReviewAndSubmit.propTypes = {
  guestInfo: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    address: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zipCode: PropTypes.string,
    cardType: PropTypes.string,
    cardNumber: PropTypes.string,
    expirationMonth: PropTypes.string,
    expirationYear: PropTypes.number,
    cvv: PropTypes.number
  }),
  roomType: PropTypes.any,
  selectedRoom: PropTypes.any,
  numberOfGuests: PropTypes.any,
  startDate: PropTypes.any,
  endDate: PropTypes.any,
  property: PropTypes.any,
  submittedAccount: submittedAccountShape,
  selectedOffers: PropTypes.arrayOf(selectedOfferShape),
  setShouldRefetch: PropTypes.func,
  isAdmin: PropTypes.bool,
  bookingSource: PropTypes.string,
  reviewAndSubmitForm: PropTypes.shape({
    alerts: PropTypes.arrayOf(PropTypes.string),
    roomFeatures: PropTypes.arrayOf(PropTypes.string),
    traces: PropTypes.arrayOf(PropTypes.string),
    discounts: PropTypes.arrayOf(PropTypes.string)
  }),
  bookingComments: PropTypes.string,
  secondSubmittedAccount: submittedAccountShape,
  isWaitListBooking: PropTypes.bool,
  waitListBookingReason: PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired
  }),
  waitListBookingComment: PropTypes.string,
  goToWaitlistConfirmationPage: PropTypes.func
};

export default reduxForm({
  form: 'reviewAndSubmit'
})(ReviewAndSubmit);
